const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForNextVideoFrame = (video) =>
  new Promise((resolve) => {
    if ("requestVideoFrameCallback" in video) {
      video.requestVideoFrameCallback(() => resolve());
    } else {
      requestAnimationFrame(() => resolve());
    }
  });

const waitForVideoMetadata = (video) =>
  new Promise((resolve, reject) => {
    // Metadata may already be available (event would have fired before we
    // attached the handler) — resolve immediately to avoid a needless wait.
    if (video.readyState >= 1) {
      resolve();
      return;
    }

    const timeout = setTimeout(() => {
      reject(new Error("Timed out waiting for screen video metadata."));
    }, 5000);

    video.onloadedmetadata = () => {
      clearTimeout(timeout);
      resolve();
    };

    video.onerror = () => {
      clearTimeout(timeout);
      reject(new Error("Unable to load screen capture video metadata."));
    };
  });

const isCanvasMostlyGreyOrBlank = (canvas) => {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  if (!ctx || !canvas.width || !canvas.height) {
    return true;
  }

  // Sample a generous region so a legit but plain page (e.g. mostly white)
  // isn't mistaken for a blank capture.
  const sampleWidth = Math.min(canvas.width, 600);
  const sampleHeight = Math.min(canvas.height, 400);

  const imageData = ctx.getImageData(0, 0, sampleWidth, sampleHeight).data;

  // A blank/grey/black capture frame is almost perfectly uniform, so it has
  // near-zero brightness variance. A real page — even a mostly-white one — has
  // text, borders and UI chrome that push the std-dev well above the threshold.
  // (The old per-pixel check treated white as "grey" and false-flagged light
  // pages, which then waited out the full timeout before falling back.)
  let sum = 0;
  let sumSquares = 0;
  let count = 0;

  for (let i = 0; i < imageData.length; i += 4) {
    const brightness =
      (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
    sum += brightness;
    sumSquares += brightness * brightness;
    count += 1;
  }

  if (!count) return true;

  const mean = sum / count;
  const variance = sumSquares / count - mean * mean;
  const stdDev = Math.sqrt(Math.max(variance, 0));

  return stdDev < 6;
};

const stopStream = (stream) => {
  if (!stream) return;
  stream.getTracks().forEach((track) => track.stop());
};

export const captureScreenWhenReady = async ({
  initialDelayMs = 1000,
  retryDelayMs = 250,
  maxWaitMs = 6000,
} = {}) => {
  let stream;

  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        cursor: "always",
      },
      audio: false,
      selfBrowserSurface: "include", // allow capturing the current tab
    });

    const video = document.createElement("video");

    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;

    // Neither of these should abort the capture: an offscreen, muted video can
    // resolve metadata before we listen, and play() can reject under autoplay
    // policy — in both cases we can still grab frames below.
    try {
      await waitForVideoMetadata(video);
    } catch (metaErr) {
      console.warn("Screen capture: metadata wait failed, continuing.", metaErr);
    }

    try {
      await video.play();
    } catch (playErr) {
      console.warn("Screen capture: video.play() rejected, continuing.", playErr);
    }

    await sleep(initialDelayMs);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    if (!ctx) {
      throw new Error("Could not create canvas context for screen capture.");
    }

    // Dimensions can lag slightly behind playback; give them a few frames
    // before giving up rather than failing the whole capture.
    let dimensionWaitMs = 0;
    while ((!video.videoWidth || !video.videoHeight) && dimensionWaitMs < 2000) {
      await sleep(retryDelayMs);
      dimensionWaitMs += retryDelayMs;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    if (!canvas.width || !canvas.height) {
      throw new Error("Screen capture returned an empty video size.");
    }

    const startedAt = Date.now();
    let dataUrl = "";

    while (Date.now() - startedAt < maxWaitMs) {
      await waitForNextVideoFrame(video);

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const isBadFrame = isCanvasMostlyGreyOrBlank(canvas);

      if (!isBadFrame) {
        dataUrl = canvas.toDataURL("image/png");
        break;
      }

      await sleep(retryDelayMs);
    }

    // Fallback: do not leave the user stuck forever.
    if (!dataUrl) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      dataUrl = canvas.toDataURL("image/png");
    }

    return dataUrl;
  } finally {
    stopStream(stream);
  }
};
